// client/src/components/wetakeover/ReviewOrder.jsx
import React from 'react';
import { PRICING } from './PricingPreview';

const ReviewOrder = ({ tier, numCreators, qrtsPerCreator, projectDetails, onProjectDetailsChange }) => {
  const calculatePricing = () => {
    const pricing = PRICING[tier];
    const totalQRTs = numCreators * qrtsPerCreator;
    const total = totalQRTs * pricing.qrtCost;

    return {
      totalQRTs,
      total,
    };
  };

  const pricing = calculatePricing();

  return (
    <div className="ui-motion-enter space-y-8 max-w-5xl mx-auto">
      <div className="text-center">
        <h2 className="text-3xl md:text-5xl font-bold mb-4">
          Campaign <span className="text-yellow-300">Review</span>
        </h2>
        <p className="text-white/70 text-lg">
          Review your campaign details before payment
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left: Campaign Summary with Pricing */}
        <div className="bg-white/5 rounded-[32px] p-8 border border-white/10 space-y-6">
          <h3 className="text-xl font-semibold text-yellow-300 mb-4">Campaign Summary</h3>

          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-white/70">Tier:</span>
              <span className="font-semibold">
                {tier === 'mid' ? 'Mid-Tier KOLs' : 'High-Tier KOLs'}
              </span>
            </div>

            <div className="flex justify-between items-center">
              <span className="text-white/70">Number of Creators:</span>
              <span className="font-semibold">{numCreators}</span>
            </div>

            <div className="flex justify-between items-center">
              <span className="text-white/70">Contents per Creator:</span>
              <span className="font-semibold">{qrtsPerCreator}</span>
            </div>

            <div className="flex justify-between items-center pt-3 border-t border-white/10">
              <span className="text-white/70">Total QRTs:</span>
              <span className="font-bold text-yellow-300">{pricing.totalQRTs}</span>
            </div>
          </div>

          <div className="h-px bg-white/10" />

          <h3 className="text-xl font-semibold text-yellow-300 mb-4">Price Breakdown</h3>

          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-white/70">
                QRTs ({pricing.totalQRTs} × ${PRICING[tier].qrtCost})
              </span>
              <span className="font-mono">${pricing.total.toLocaleString()}</span>
            </div>

            <div className="border-t border-white/10 pt-4 mt-4">
              <div className="flex justify-between items-center">
                <span className="text-xl font-bold">Total</span>
                <span className="text-3xl font-bold text-yellow-300 font-mono">
                  ${pricing.total.toLocaleString()}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Right: Campaign Details Form */}
        <div className="bg-white/5 rounded-[32px] p-8 border border-white/10 space-y-6">
          <h3 className="text-xl font-semibold text-yellow-300 mb-4">Campaign Details</h3>

          <div className="space-y-4">
            {/* Project Name */}
            <div>
              <label className="block text-white font-semibold mb-2">
                Project Name <span className="text-yellow-300">*</span>
              </label>
              <input
                type="text"
                value={projectDetails.projectName}
                onChange={(e) =>
                  onProjectDetailsChange({ ...projectDetails, projectName: e.target.value })
                }
                className="w-full bg-white/5 border border-white/10 rounded-2xl px-4 py-3 text-white placeholder:text-white/40 focus:border-yellow-300 focus:outline-none transition-colors backdrop-blur-md"
                placeholder="Enter your project name"
              />
            </div>

            {/* Twitter Handle */}
            <div>
              <label className="block text-white font-semibold mb-2">
                X (Twitter) Handle <span className="text-yellow-300">*</span>
              </label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-white/50">@</span>
                <input
                  type="text"
                  value={projectDetails.twitterHandle || ''}
                  onChange={(e) =>
                    onProjectDetailsChange({ ...projectDetails, twitterHandle: e.target.value.replace('@', '') })
                  }
                  className="w-full bg-white/5 border border-white/10 rounded-2xl pl-10 pr-4 py-3 text-white placeholder:text-white/40 focus:border-yellow-300 focus:outline-none transition-colors backdrop-blur-md"
                  placeholder="yourproject"
                />
              </div>
            </div>

            {/* Briefing Material */}
            <div>
              <label className="block text-white font-semibold mb-2">Briefing Material (if needed)</label>
              <textarea
                rows="3"
                value={projectDetails.briefingMaterial || ''}
                onChange={(e) =>
                  onProjectDetailsChange({ ...projectDetails, briefingMaterial: e.target.value })
                }
                className="w-full bg-white/5 border border-white/10 rounded-2xl px-4 py-3 text-white placeholder:text-white/40 focus:border-yellow-300 focus:outline-none transition-colors backdrop-blur-md resize-none"
                placeholder="Links to briefing docs, brand guidelines, etc."
              />
            </div>

            {/* Instructions */}
            <div>
              <label className="block text-white font-semibold mb-2">
                Instructions to the Campaign <span className="text-yellow-300">*</span>
              </label>
              <textarea
                rows="4"
                value={projectDetails.instructions || ''}
                onChange={(e) =>
                  onProjectDetailsChange({ ...projectDetails, instructions: e.target.value })
                }
                className="w-full bg-white/5 border border-white/10 rounded-2xl px-4 py-3 text-white placeholder:text-white/40 focus:border-yellow-300 focus:outline-none transition-colors backdrop-blur-md resize-none"
                placeholder="Specific instructions, messaging guidelines, hashtags to use, etc."
              />
            </div>

            <p className="text-xs text-white/50">* Required fields</p>
          </div>
        </div>
      </div>

      {/* Payment Methods */}
      <div className="bg-gradient-to-r from-yellow-300/10 to-yellow-300/5 rounded-[24px] p-6 border border-yellow-300/20">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-full bg-yellow-300 flex items-center justify-center text-black">
            {/* ETH Icon */}
            <svg className="w-6 h-6" viewBox="0 0 256 417" fill="currentColor">
              <path d="M127.961 0l-2.795 9.5v275.668l2.795 2.79 127.962-75.638z" opacity="0.6"/>
              <path d="M127.962 0L0 212.32l127.962 75.639V154.158z"/>
              <path d="M127.961 312.187l-1.575 1.92v98.199l1.575 4.6L256 236.587z" opacity="0.6"/>
              <path d="M127.962 416.905v-104.72L0 236.585z"/>
              <path d="M127.961 287.958l127.96-75.637-127.96-58.162z" opacity="0.45"/>
              <path d="M0 212.32l127.96 75.638v-133.8z" opacity="0.8"/>
            </svg>
          </div>
          <span className="font-semibold">Accepted Payment Currencies on Ethereum Mainnet</span>
        </div>
        <div className="flex gap-4 text-sm text-white/70">
          <span>• USDC (ERC-20)</span>
          <span>• USDT (ERC-20)</span>
        </div>
      </div>
    </div>
  );
};

export default ReviewOrder;
